import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  HelpCircle,
  RefreshCw,
  Terminal,
  XCircle,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// Timed subtitle segments
const DRAFT_SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 110,
    text: "Pernah gak lo ngerasa, kenapa AI lo masih sering bego dan ngasih jawaban ngawur,",
    highlight: "masih sering bego",
  },
  {
    from: 110,
    to: 180,
    text: "padahal lo udah langganan model paling mahal tiap bulan?",
    highlight: "langganan model paling mahal",
  },
  {
    from: 180,
    to: 290,
    text: "Masalahnya bukan di kepintaran modelnya, tapi cara lo memperlakukan mereka.",
    highlight: "cara lo memperlakukan mereka",
  },
  {
    from: 290,
    to: 410,
    text: "Kebanyakan orang cuma pakai AI sebagai chatbot tanya-jawab sekali lempar.",
    highlight: "chatbot tanya-jawab sekali lempar",
  },
  {
    from: 410,
    to: 510,
    text: "Begitu lo tutup tab browser, memorinya hilang dan dia gak bisa ngapa-ngapain selain nebak kata.",
    highlight: "memorinya hilang",
  },
  {
    from: 510,
    to: 640,
    text: "Beda cerita pas lo ubah polanya jadi Autonomous AI Agent.",
    highlight: "Autonomous AI Agent",
  },
  {
    from: 640,
    to: 770,
    text: "Di sini AI lo dikasih memori kerja, akses file komputer, dan alur evaluasi sendiri.",
    highlight: "memori kerja & akses file",
  },
  {
    from: 770,
    to: 910,
    text: "Begitu lo lempar satu masalah, dia gak cuma jawab teori,",
    highlight: "gak cuma jawab teori",
  },
  {
    from: 910,
    to: 1040,
    text: "tapi dia ngetes kodenya, benerin error-nya sendiri, baru ngasih hasil final yang valid.",
    highlight: "benerin error-nya sendiri",
  },
  {
    from: 1040,
    to: 1180,
    text: "Berhenti pakai AI cuma buat nanya resep atau ide caption receh.",
    highlight: "Berhenti pakai AI buat caption",
  },
  {
    from: 1180,
    to: 1350,
    text: "Follow @zura.ailab buat liat gimana gue ngerakit sistem AI yang beneran kerja di laptop.",
    highlight: "Follow @zura.ailab",
  },
];

// FONT DECLARATIONS VIA STATICFILE
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'RajdhaniDisplay';
  src: url('${staticFile("fonts/Rajdhani-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'ChakraDisplay';
  src: url('${staticFile("fonts/ChakraPetch-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
`;

export const ZuraAIRealityCheck: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy spring helper
  const sp = (delay: number, damping = 16, stiffness = 130) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness },
    });

  // Scene transitions
  const s1Exit = interpolate(frame, [170, 190], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s1Opacity = 1 - s1Exit;
  const s1Y = interpolate(s1Exit, [0, 1], [0, -32]);

  const s2Exit = interpolate(frame, [490, 510], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Enter = interpolate(frame, [180, 200], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Opacity = s2Enter * (1 - s2Exit);
  const s2Y = interpolate(s2Exit, [0, 1], [0, -32]);

  const s3Exit = interpolate(frame, [820, 840], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Enter = interpolate(frame, [500, 520], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Opacity = s3Enter * (1 - s3Exit);
  const s3Y = interpolate(s3Exit, [0, 1], [0, -32]);

  const s4Exit = interpolate(frame, [1130, 1150], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Enter = interpolate(frame, [830, 850], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Opacity = s4Enter * (1 - s4Exit);
  const s4Y = interpolate(s4Exit, [0, 1], [0, -32]);

  const s5Enter = interpolate(frame, [1140, 1160], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s5Opacity = s5Enter;

  // Active step highlight for Scene 4 workflow
  const activeStep = frame < 930 ? 1 : frame < 1030 ? 2 : 3;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#F6F4EB", // OpenDesign Warm Paper
        color: "#1C1917",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* AUDIO: Ducked BGM */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.08} loop />

      {/* TACTILE PAPER GRAIN & FINE PIN-BOARD DOT GRID */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(28, 25, 23, 0.08) 1.5px, transparent 1.5px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      {/* SUBTLE PAPER VIGNETTE BORDER */}
      <div
        style={{
          position: "absolute",
          inset: 24,
          border: "2.5px dashed rgba(28, 25, 23, 0.16)",
          borderRadius: 40,
          pointerEvents: "none",
        }}
      />

      {/* TOP HEADER BRAND CAPSULE (SAFE ZONE Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 80,
          transform: `translateY(${interpolate(sp(0, 18, 120), [0, 1], [-20, 0])}px)`,
          opacity: sp(0, 18, 120),
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "#FFFFFF",
            padding: "12px 28px",
            borderRadius: 999,
            border: "3px solid #1C1917",
            boxShadow: "5px 5px 0px #1C1917",
          }}
        >
          <Brain size={24} color="#EA580C" strokeWidth={2.5} />
          <span
            style={{
              fontFamily: "'RajdhaniDisplay', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 2,
              color: "#1C1917",
              textTransform: "uppercase",
            }}
          >
            THE AI REALITY CHECK · @ZURA.AILAB
          </span>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#EA580C",
            }}
          />
        </div>
      </div>

      {/* ========================================================= */}
      {/* EDITORIAL STAGE CANVAS (Y: 340px - 1420px, WIDTH: 880px)  */}
      {/* Centered with generous breathing room & Swiss typography  */}
      {/* ========================================================= */}
      <div
        style={{
          position: "absolute",
          top: 340,
          bottom: 500,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 880,
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* ========================================================= */}
          {/* SCENE 1: THE HOOK — KENAPA AI LO BEGO? (0 - 190)          */}
          {/* ========================================================= */}
          {frame < 195 && (
            <div
              style={{
                opacity: s1Opacity,
                transform: `translateY(${s1Y}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 24,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center", width: "100%" }}>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 88,
                    lineHeight: 0.95,
                    letterSpacing: 1.5,
                    color: "#1C1917",
                    textTransform: "uppercase",
                    transform: `translateY(${interpolate(sp(2, 18, 120), [0, 1], [30, 0])}px)`,
                  }}
                >
                  KENAPA AI LO MASIH BEGO?
                </div>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 32,
                    fontWeight: 700,
                    letterSpacing: 2,
                    color: "#DC2626",
                    textTransform: "uppercase",
                    marginTop: 12,
                  }}
                >
                  PADAHAL LANGGANAN MODEL PALING MAHAL
                </div>
              </div>

              {/* Paradox Card (Expanded & Bolder) */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "3.5px solid #1C1917",
                  borderRadius: 28,
                  padding: "32px 36px",
                  boxShadow: "8px 8px 0px #1C1917",
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 20,
                    backgroundColor: "#FEF2F2",
                    border: "3px solid #DC2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <HelpCircle size={40} color="#DC2626" />
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 26,
                      fontWeight: 700,
                      color: "#991B1B",
                      letterSpacing: 1,
                    }}
                  >
                    PARADOKS PENGGUNA AI 2026
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 600,
                      color: "#374151",
                      marginTop: 6,
                      lineHeight: 1.35,
                    }}
                  >
                    Jawaban halusinasi, lupa riwayat obrolan, dan gak bisa eksekusi tugas nyata sendirian.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 2: KESALAHAN CHATBOT BIASA (180 - 510)              */}
          {/* ========================================================= */}
          {frame >= 175 && frame < 515 && (
            <div
              style={{
                opacity: s2Opacity,
                transform: `translateY(${s2Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#DC2626",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                  }}
                >
                  DIAGNOSIS AKAR MASALAH
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 68,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  POLA LAMA: CHATBOT TANYA-JAWAB
                </div>
              </div>

              {/* Card Limitasi Chatbot (Massive Text & High Contrast) */}
              <div
                style={{
                  backgroundColor: "#FEF2F2",
                  border: "4px solid #DC2626",
                  borderRadius: 28,
                  padding: "32px 36px",
                  boxShadow: "8px 8px 0px #DC2626",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <XCircle size={32} color="#DC2626" />
                    <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 40, color: "#991B1B" }}>
                      3 PENYAKIT CHATBOT BIASA
                    </span>
                  </div>
                  <span
                    style={{
                      backgroundColor: "#DC2626",
                      color: "#FFFFFF",
                      fontSize: 16,
                      fontWeight: 800,
                      padding: "6px 14px",
                      borderRadius: 8,
                      letterSpacing: 1,
                    }}
                  >
                    SEKALI LEMPAR
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    fontSize: 24,
                    fontWeight: 600,
                    color: "#7F1D1D",
                    lineHeight: 1.4,
                  }}
                >
                  <div>• <b>Amnesia Konteks:</b> Tutup browser = ingatan reset dari nol.</div>
                  <div>• <b>Nol Akses Alat:</b> Gak bisa buka file, baca folder, atau jalanin skrip.</div>
                  <div>• <b>Prediksi Buta:</b> Cuma nebak probabilitas token teks berikutnya.</div>
                </div>
              </div>

              {/* Friction Tag */}
              <div
                style={{
                  backgroundColor: "#1C1917",
                  color: "#FFFFFF",
                  borderRadius: 20,
                  padding: "16px 24px",
                  textAlign: "center",
                  fontFamily: "'RajdhaniDisplay', sans-serif",
                  fontSize: 26,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  boxShadow: "6px 6px 0px rgba(0,0,0,0.25)",
                }}
              >
                HASILNYA: LO CAPEK COPY-PASTE & BENERIN MANUAL
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 3: AUTONOMOUS AGENT STACK (500 - 840)               */}
          {/* ========================================================= */}
          {frame >= 495 && frame < 845 && (
            <div
              style={{
                opacity: s3Opacity,
                transform: `translateY(${s3Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#059669",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                  }}
                >
                  PARADIGMA BARU 2026
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 68,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  AUTONOMOUS AI AGENT
                </div>
              </div>

              {/* Card Autonomous Agent Stack (Massive Text & High Contrast) */}
              <div
                style={{
                  backgroundColor: "#ECFDF5",
                  border: "4px solid #059669",
                  borderRadius: 28,
                  padding: "32px 36px",
                  boxShadow: "8px 8px 0px #059669",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <CheckCircle2 size={32} color="#059669" />
                    <span style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 40, color: "#065F46" }}>
                      3 PILAR UTAMA AI AGENT
                    </span>
                  </div>
                  <span
                    style={{
                      backgroundColor: "#059669",
                      color: "#FFFFFF",
                      fontSize: 16,
                      fontWeight: 800,
                      padding: "6px 14px",
                      borderRadius: 8,
                      letterSpacing: 1,
                    }}
                  >
                    SISTEM MANDIRI
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    fontSize: 24,
                    fontWeight: 600,
                    color: "#064E3B",
                    lineHeight: 1.4,
                  }}
                >
                  <div>• <b>Persistent Memory:</b> Ingat aturan proyek & riwayat kerja kemarin.</div>
                  <div>• <b>Tool Arsenal:</b> Punya terminal, baca berkas lokal, & eksekusi API.</div>
                  <div>• <b>Self-Correction:</b> Ngetes kodenya sendiri sampai hijau sebelum lapor.</div>
                </div>
              </div>

              {/* Badge Advantage */}
              <div
                style={{
                  backgroundColor: "#059669",
                  color: "#FFFFFF",
                  borderRadius: 20,
                  padding: "16px 24px",
                  textAlign: "center",
                  fontFamily: "'RajdhaniDisplay', sans-serif",
                  fontSize: 26,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  boxShadow: "6px 6px 0px #1C1917",
                }}
              >
                BUKAN MESIN JAWAB TEORI · TAPI MESIN PENYELESAI TUGAS
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 4: ALUR EKSEKUSI NYATA (830 - 1150)                 */}
          {/* ========================================================= */}
          {frame >= 825 && frame < 1155 && (
            <div
              style={{
                opacity: s4Opacity,
                transform: `translateY(${s4Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#1C1917",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 8,
                  }}
                >
                  ALUR KERJA OTONOM
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 72,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    textTransform: "uppercase",
                  }}
                >
                  LEMPAR 1 TARGET · SELESAI TUNTAS
                </div>
              </div>

              {/* 3 Full-Width Sequential Node Cards (Bigger & Super Clear) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
                {/* Node 1: Eksekusi */}
                <div
                  style={{
                    backgroundColor: activeStep === 1 ? "#FFF7ED" : "#FFFFFF",
                    border: activeStep === 1 ? "3.5px solid #EA580C" : "2.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: activeStep === 1 ? "6px 6px 0px #EA580C" : "4px 4px 0px #1C1917",
                    transform: activeStep === 1 ? "scale(1.02)" : "scale(1.0)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        backgroundColor: "#EA580C",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FFFFFF",
                      }}
                    >
                      <Terminal size={28} color="#FFFFFF" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        1. EKSEKUSI TUGAS
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Agen bikin kode, baca berkas, & jalankan tools nyata
                      </div>
                    </div>
                  </div>
                  {activeStep === 1 && (
                    <span style={{ backgroundColor: "#EA580C", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "6px 14px", borderRadius: 8, letterSpacing: 1 }}>
                      ACTIVE
                    </span>
                  )}
                </div>

                {/* Node 2: Self-Repair */}
                <div
                  style={{
                    backgroundColor: activeStep === 2 ? "#EFF6FF" : "#FFFFFF",
                    border: activeStep === 2 ? "3.5px solid #2563EB" : "2.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: activeStep === 2 ? "6px 6px 0px #2563EB" : "4px 4px 0px #1C1917",
                    transform: activeStep === 2 ? "scale(1.02)" : "scale(1.0)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        backgroundColor: "#2563EB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FFFFFF",
                      }}
                    >
                      <RefreshCw size={28} color="#FFFFFF" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        2. SELF-REPAIR LOOP
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Error dievaluasi dan dibetulkan sendiri sampai lolos
                      </div>
                    </div>
                  </div>
                  {activeStep === 2 && (
                    <span style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "6px 14px", borderRadius: 8, letterSpacing: 1 }}>
                      REPAIRING
                    </span>
                  )}
                </div>

                {/* Node 3: Hasil Valid */}
                <div
                  style={{
                    backgroundColor: activeStep === 3 ? "#ECFDF5" : "#FFFFFF",
                    border: activeStep === 3 ? "3.5px solid #059669" : "2.5px solid #1C1917",
                    borderRadius: 22,
                    padding: "20px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: activeStep === 3 ? "6px 6px 0px #059669" : "4px 4px 0px #1C1917",
                    transform: activeStep === 3 ? "scale(1.02)" : "scale(1.0)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        backgroundColor: "#059669",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FFFFFF",
                      }}
                    >
                      <CheckCircle2 size={28} color="#FFFFFF" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 32, color: "#1C1917", lineHeight: 1.0 }}>
                        3. HASIL VALID 100%
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#4B5563", marginTop: 4 }}>
                        Artefak kerja final yang teruji dan siap pakai
                      </div>
                    </div>
                  </div>
                  {activeStep === 3 && (
                    <span style={{ backgroundColor: "#059669", color: "#FFFFFF", fontSize: 14, fontWeight: 800, padding: "6px 14px", borderRadius: 8, letterSpacing: 1 }}>
                      VERIFIED
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 5: OUTRO & CALL TO ACTION (1140 - 1350)             */}
          {/* ========================================================= */}
          {frame >= 1135 && (
            <div
              style={{
                opacity: s5Opacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 22,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 26,
                    fontWeight: 700,
                    letterSpacing: 2,
                    color: "#DC2626",
                    textTransform: "uppercase",
                  }}
                >
                  STOP PAKAI AI CUMA BUAT CAPTION RECEH
                </div>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 80,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    marginTop: 8,
                    textTransform: "uppercase",
                  }}
                >
                  BANGUN SISTEM YANG KERJA
                </div>
              </div>

              {/* 3 Big Benefit Pills */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, width: "100%" }}>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>01 · MEMORI AKTIF</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>Gak Pernah Amnesia</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>02 · EKSEKUSI NYATA</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>Terminal & File Lokal</div>
                </div>
                <div style={{ flex: 1, backgroundColor: "#FFFFFF", border: "3px solid #1C1917", borderRadius: 20, padding: "16px 14px", textAlign: "center", boxShadow: "5px 5px 0px #1C1917" }}>
                  <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 28, color: "#1C1917" }}>03 · MANDIRI TUNTAS</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#6B7280", marginTop: 2 }}>Self-Correction Loop</div>
                </div>
              </div>

              {/* Massive CTA Box */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "4px solid #1C1917",
                  borderRadius: 28,
                  padding: "24px 32px",
                  width: "100%",
                  boxShadow: "10px 10px 0px #1C1917",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxSizing: "border-box",
                  marginTop: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <div
                    style={{
                      width: 68,
                      height: 68,
                      borderRadius: "50%",
                      backgroundColor: "#000000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "3px solid #1C1917",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontFamily: "monospace", fontSize: 30, fontWeight: 900, color: "#00F0FF" }}>&#123;</span>
                    <span style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, fontWeight: 900, color: "#FFFFFF", marginLeft: 1 }}>Z</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'BebasDisplay', sans-serif", fontSize: 38, lineHeight: 1.0, color: "#1C1917" }}>
                      ZURA · AI & FUTURE TECH
                    </div>
                    <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, color: "#059669", marginTop: 4 }}>
                      @ZURA.AILAB · AGENT & MOTION
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    padding: "16px 28px",
                    borderRadius: 999,
                    border: "3px solid #1C1917",
                    boxShadow: "4px 4px 0px #1C1917",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                  }}
                >
                  <span>PANTAU SISTEMNYA</span>
                  <ArrowRight size={26} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC TIMED SUBTITLE (ANCHORED AT BOTTOM: 490px / SAFE ZONE Y: 1430px) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={DRAFT_SUBTITLES}
        bottom={490}
        fontSize={34}
        highlightColor="#F59E0B"
        backgroundColor="rgba(28, 25, 23, 0.94)"
      />
    </AbsoluteFill>
  );
};
